// src/components/Services.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectCreative, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-creative';
import 'swiper/css/navigation';

const Section = styled.section`
    padding: 120px 0;
    position: relative;
    overflow: hidden;
`;

const ServicesSection = styled(Section)`
    background: linear-gradient(135deg,
    #f8fafc 0%,
    #ffffff 50%,
    #f0f4f8 100%);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg,
        transparent 0%,
        rgba(102, 126, 234, 0.3) 50%,
        transparent 100%);
    }
`;

const BackgroundPattern = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.03;
    background-image:
            radial-gradient(circle at 25% 25%, #667eea 2px, transparent 0),
            radial-gradient(circle at 75% 75%, #764ba2 2px, transparent 0);
    background-size: 60px 60px;
`;

const SectionHeader = styled.div`
    text-align: center;
    max-width: 800px;
    margin: 0 auto 4rem;
    padding: 0 2rem;
`;

const SectionTitle = styled(motion.h2)`
    font-size: 4rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, #1a1a1a 0%, #667eea 50%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.1;
    position: relative;

    @media (max-width: 1200px) {
        font-size: 3.5rem;
    }

    @media (max-width: 768px) {
        font-size: 2.8rem;
    }

    @media (max-width: 480px) {
        font-size: 2.2rem;
    }
`;

const TitleUnderline = styled(motion.div)`
    width: 100px;
    height: 4px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 2px;
    margin: 0 auto 2rem;
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1.4rem;
    color: #4a5568;
    line-height: 1.7;
    font-weight: 400;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.8);
`;

const ContentContainer = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 3rem;

    @media (max-width: 768px) {
        padding: 0 1.5rem;
    }
`;

const SlideshowContainer = styled.div`
    width: 80%;
    margin: 0 auto 4rem;
    position: relative;

    @media (max-width: 1200px) {
        width: 90%;
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const PhotoGallerySection = styled(motion.div)`
    position: relative;
    border-radius: 30px;
    overflow: hidden;
    box-shadow: 0 35px 70px rgba(0, 0, 0, 0.25);
    height: 600px;

    @media (max-width: 1200px) {
        height: 500px;
    }

    @media (max-width: 768px) {
        height: 400px;
    }
`;

const StyledSwiper = styled(Swiper)`
    width: 100%;
    height: 100%;
    border-radius: 30px;

    .swiper-slide {
        position: relative;
        border-radius: 30px;
        overflow: hidden;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 1.5s ease;
        }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 50%;
            background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
            pointer-events: none;
        }
    }

    .swiper-slide-active img {
        transform: scale(1.1);
    }

    .swiper-button-next,
    .swiper-button-prev {
        color: white;
        background: rgba(255, 255, 255, 0.2);
        width: 70px;
        height: 70px;
        border-radius: 50%;
        backdrop-filter: blur(15px);
        margin-top: -35px;

        &::after {
            font-size: 1.8rem;
            font-weight: bold;
        }

        &:hover {
            background: rgba(255, 255, 255, 0.3);
            transform: scale(1.1);
        }
    }

    .swiper-pagination-bullet {
        background: rgba(255, 255, 255, 0.6);
        opacity: 1;
        width: 16px;
        height: 16px;
        transition: all 0.3s ease;

        &-active {
            background: white;
            transform: scale(1.4);
        }
    }
`;

const SlideCaption = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 3rem 4rem;
    color: white;
    z-index: 3;

    h4 {
        font-size: 2rem;
        font-weight: 700;
        margin: 0 0 1rem 0;
        text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
    }

    p {
        font-size: 1.3rem;
        margin: 0;
        opacity: 0.9;
        text-shadow: 0 2px 6px rgba(0, 0, 0, 0.7);
    }

    @media (max-width: 768px) {
        padding: 2rem;

        h4 {
            font-size: 1.5rem;
        }

        p {
            font-size: 1.1rem;
        }
    }
`;

const ContentBelowSlideshow = styled.div`
    max-width: 1200px;
    margin: 0 auto;
`;

const MainContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;
    margin-bottom: 4rem;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 3rem;
    }
`;

const GuideText = styled.div`
    h3 {
        font-size: 2.5rem;
        font-weight: 800;
        margin-bottom: 2rem;
        background: linear-gradient(135deg, #1a1a1a 0%, #667eea 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1.2;

        @media (max-width: 768px) {
            font-size: 2rem;
        }
    }

    p {
        font-size: 1.2rem;
        color: #4a5568;
        line-height: 1.7;
        margin-bottom: 2rem;
        font-weight: 400;
    }
`;

const GuideHighlight = styled(motion.div)`
    background: linear-gradient(135deg,
    rgba(102, 126, 234, 0.95) 0%,
    rgba(118, 75, 162, 0.95) 100%);
    color: white;
    padding: 2.5rem;
    border-radius: 20px;
    margin: 2.5rem 0;
    box-shadow:
            0 20px 40px rgba(102, 126, 234, 0.3),
            0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:
                radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
        pointer-events: none;
    }

    p {
        font-size: 1.3rem;
        font-weight: 500;
        line-height: 1.7;
        margin: 0;
        font-style: italic;
        position: relative;
        z-index: 2;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
`;

const FeaturesSection = styled.div`
    background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.95) 100%);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 25px;
    padding: 3rem;
    box-shadow:
            0 20px 40px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(255, 255, 255, 0.6) inset;

    @media (max-width: 768px) {
        padding: 2rem;
    }
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
`;

const FeatureCard = styled(motion.div)`
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 16px;
    padding: 2rem;
    display: flex;
    align-items: center;
    gap: 1.5rem;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;

    &:hover {
        transform: translateX(5px);
        box-shadow: 0 12px 35px rgba(0, 0, 0, 0.1);
        border-color: rgba(102, 126, 234, 0.3);
    }

    .feature-icon {
        font-size: 2.5rem;
        flex-shrink: 0;
    }

    .feature-content {
        flex: 1;
    }

    .feature-title {
        font-size: 1.3rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
        color: #1a1a1a;
    }

    .feature-desc {
        color: #4a5568;
        line-height: 1.6;
        font-size: 1rem;
    }
`;

const AdditionalContent = styled(motion.div)`
    background: linear-gradient(135deg, 
        rgba(102, 126, 234, 0.05) 0%, 
        rgba(118, 75, 162, 0.05) 100%);
    border-radius: 25px;
    padding: 3rem;
    text-align: center;
    border: 1px solid rgba(102, 126, 234, 0.1);

    p {
        font-size: 1.3rem;
        color: #4a5568;
        line-height: 1.7;
        margin-bottom: 0;
        font-weight: 400;
    }

    @media (max-width: 768px) {
        padding: 2rem;
    }
`;

const FloatingElements = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
`;

const FloatingElement = styled(motion.div)`
    position: absolute;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 20px;
    opacity: 0.1;
    
    &.element-1 {
        width: 120px;
        height: 120px;
        top: 10%;
        right: 5%;
    }
    
    &.element-2 {
        width: 80px;
        height: 80px;
        bottom: 20%;
        left: 3%;
    }
`;

const Services: React.FC = () => {
    const guidePhotos = [
        {
            image: '/w1.jpeg',
            title: 'Personal City Tours',
            description: 'Exploring historic districts with our guests'
        },
        {
            image: '/w2.jpeg',
            title: 'Mountain Adventures',
            description: 'Guided hikes through breathtaking landscapes'
        },
        {
            image: '/w3.jpeg',
            title: 'Cultural Experiences',
            description: 'Sharing local traditions and customs'
        },
        {
            image: '/w4.jpeg',
            title: 'Castle Explorations',
            description: 'Discovering medieval history together'
        },
        {
            image: '/w5.jpeg',
            title: 'Local Cuisine',
            description: 'Introducing authentic regional dishes'
        },
        {
            image: '/w6.jpeg',
            title: 'Scenic Views',
            description: 'Guiding to the best panoramic spots'
        },
        {
            image: '/w7.jpeg',
            title: 'Group Activities',
            description: 'Creating memorable group experiences'
        }
    ];

    const features = [
        {
            icon: '👨‍💼',
            title: 'Personal Guides',
            description: 'Dedicated expert guides who become your travel companions'
        },
        {
            icon: '🗺️',
            title: 'Custom Itineraries',
            description: 'Tailored routes based on your interests and preferences'
        },
        {
            icon: '🏛️',
            title: 'Local Insights',
            description: 'Access to hidden gems and authentic local experiences'
        },
        {
            icon: '📱',
            title: '24/7 Support',
            description: 'Round-the-clock assistance throughout your journey'
        }
    ];

    return (
        <ServicesSection id="services">
            <BackgroundPattern />

            <FloatingElements>
                <FloatingElement
                    className="element-1"
                    animate={{
                        y: [0, -40, 0],
                        rotate: [0, 180, 360]
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
                <FloatingElement
                    className="element-2"
                    animate={{
                        y: [0, 25, 0],
                        rotate: [0, -90, 0]
                    }}
                    transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                    }}
                />
            </FloatingElements>

            <SectionHeader>
                <SectionTitle
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    viewport={{ once: true, margin: "-100px" }}
                >
                    Personal Guide Experience
                </SectionTitle>

                <TitleUnderline
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                />

                <SectionSubtitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                    Journey with experts who don't just show you places, but create unforgettable experiences
                    filled with personal connections and authentic moments
                </SectionSubtitle>
            </SectionHeader>

            <ContentContainer>
                {/* Main Slideshow - 80% width */}
                <SlideshowContainer>
                    <PhotoGallerySection
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        viewport={{ once: true }}
                    >
                        <StyledSwiper
                            modules={[Autoplay, EffectCreative, Navigation]}
                            effect="creative"
                            creativeEffect={{
                                prev: {
                                    shadow: true,
                                    translate: ["-120%", 0, -500],
                                },
                                next: {
                                    shadow: true,
                                    translate: ["120%", 0, -500],
                                },
                            }}
                            speed={1200}
                            autoplay={{
                                delay: 4000,
                                disableOnInteraction: false,
                                pauseOnMouseEnter: true,
                            }}
                            navigation={true}
                            loop={true}
                            spaceBetween={0}
                            slidesPerView={1}
                            pagination={{ clickable: true }}
                        >
                            {guidePhotos.map((photo, index) => (
                                <SwiperSlide key={index}>
                                    <img
                                        src={photo.image}
                                        alt={photo.title}
                                        onError={(e) => {
                                            // Fallback to Unsplash if images not found
                                            e.currentTarget.src = `https://images.unsplash.com/photo-${1500000000000 + index}?w=1200&h=800&fit=crop`;
                                        }}
                                    />
                                    <SlideCaption>
                                        <h4>{photo.title}</h4>
                                        <p>{photo.description}</p>
                                    </SlideCaption>
                                </SwiperSlide>
                            ))}
                        </StyledSwiper>
                    </PhotoGallerySection>
                </SlideshowContainer>

                {/* Content Below Slideshow */}
                <ContentBelowSlideshow>
                    <MainContentGrid>
                        <GuideText>
                            <motion.h3
                                initial={{ opacity: 0, x: -30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                viewport={{ once: true }}
                            >
                                Your Journey, Our Passion
                            </motion.h3>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                At DRL Trips, we believe the best travel experiences happen when you have
                                a knowledgeable local by your side. Our expert guides become your travel companions,
                                sharing not just the sights, but the stories, culture and hidden treasures that
                                make Central Europe truly magical.
                            </motion.p>

                            <GuideHighlight
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                viewport={{ once: true }}
                                whileHover={{ scale: 1.02 }}
                            >
                                <p>
                                    "We don't just guide you through destinations—we walk alongside you,
                                    creating personal connections and memories that last long after you return home."
                                </p>
                            </GuideHighlight>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                Whether exploring ancient castles, hiking through majestic mountains,
                                or discovering local culinary delights, our guides ensure every moment
                                is filled with wonder, comfort and authentic connection.
                            </motion.p>
                        </GuideText>

                        <FeaturesSection>
                            <motion.h3
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                viewport={{ once: true }}
                                style={{
                                    fontSize: '2rem',
                                    fontWeight: 700,
                                    marginBottom: '2rem',
                                    background: 'linear-gradient(135deg, #1a1a1a 0%, #667eea 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    textAlign: 'center'
                                }}
                            >
                                What We Offer
                            </motion.h3>
                            <FeaturesGrid>
                                {features.map((feature, index) => (
                                    <FeatureCard
                                        key={index}
                                        initial={{ opacity: 0, x: 20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                                        viewport={{ once: true }}
                                        whileHover={{ x: 5 }}
                                    >
                                        <div className="feature-icon">{feature.icon}</div>
                                        <div className="feature-content">
                                            <div className="feature-title">{feature.title}</div>
                                            <div className="feature-desc">{feature.description}</div>
                                        </div>
                                    </FeatureCard>
                                ))}
                            </FeaturesGrid>
                        </FeaturesSection>
                    </MainContentGrid>

                    <AdditionalContent
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <p>
                            Ready to experience Central Europe with a personal guide who truly cares about your journey?
                            Contact us today to start planning your unforgettable adventure.
                        </p>
                    </AdditionalContent>
                </ContentBelowSlideshow>
            </ContentContainer>
        </ServicesSection>
    );
};

export default Services;